import { AMPLITUDE_THRESHOLD, FFT_SIZE, SAMPLE_RATE } from './constants';

export interface AudioProcessor {
  start(): Promise<void>;
  stop(): void;
  getFrequency(): number | null;
  isActive(): boolean;
}

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let mediaStream: MediaStream | null = null;
let source: MediaStreamAudioSourceNode | null = null;
let active = false;

// Buffers reused across calls
let timeDomainBuffer: Float32Array<ArrayBuffer> | null = null;

export async function requestMicrophoneAccess(): Promise<void> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('UNSUPPORTED');
  }

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
  } catch (err: any) {
    if (
      err.name === 'NotAllowedError' ||
      err.name === 'PermissionDeniedError'
    ) {
      throw new Error('DENIED');
    }
    throw err;
  }

  audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
  analyser = audioContext.createAnalyser();
  analyser.fftSize = FFT_SIZE;
  analyser.smoothingTimeConstant = 0;

  source = audioContext.createMediaStreamSource(mediaStream);
  source.connect(analyser);

  timeDomainBuffer = new Float32Array(analyser.fftSize);
}

export function createAudioProcessor(): AudioProcessor {
  return {
    start() {
      active = true;
      if (audioContext?.state === 'suspended') {
        return audioContext.resume();
      }
      return Promise.resolve();
    },

    stop() {
      active = false;
    },

    isActive() {
      return active;
    },

    getFrequency(): number | null {
      if (!active || !analyser || !timeDomainBuffer) return null;

      analyser.getFloatTimeDomainData(timeDomainBuffer);

      // Check amplitude (RMS)
      let rms = 0;
      for (let i = 0; i < timeDomainBuffer.length; i++) {
        rms += timeDomainBuffer[i] * timeDomainBuffer[i];
      }
      rms = Math.sqrt(rms / timeDomainBuffer.length);

      if (rms < AMPLITUDE_THRESHOLD) return null;

      return detectPitchYIN(timeDomainBuffer, audioContext!.sampleRate);
    },
  };
}

/**
 * YIN pitch detection algorithm (simplified).
 * Returns fundamental frequency in Hz, or null if no clear pitch.
 */
function detectPitchYIN(
  buffer: Float32Array,
  sampleRate: number,
): number | null {
  const bufferSize = buffer.length;
  const halfSize = Math.floor(bufferSize / 2);

  // Step 1 & 2: Difference function + cumulative mean normalized difference
  const yinBuffer = new Float32Array(halfSize);

  // Difference function
  for (let tau = 0; tau < halfSize; tau++) {
    let sum = 0;
    for (let i = 0; i < halfSize; i++) {
      const delta = buffer[i] - buffer[i + tau];
      sum += delta * delta;
    }
    yinBuffer[tau] = sum;
  }

  // Cumulative mean normalized difference
  yinBuffer[0] = 1;
  let runningSum = 0;
  for (let tau = 1; tau < halfSize; tau++) {
    runningSum += yinBuffer[tau];
    yinBuffer[tau] *= tau / runningSum;
  }

  // Step 3: Absolute threshold - find first dip below threshold
  const threshold = 0.15;
  let tauEstimate = -1;

  // Min tau corresponds to max frequency we care about (~5000 Hz)
  const minTau = Math.floor(sampleRate / 5000);
  // Max tau corresponds to min frequency we care about (~25 Hz)
  const maxTau = Math.min(Math.floor(sampleRate / 25), halfSize - 1);

  for (let tau = minTau; tau < maxTau; tau++) {
    if (yinBuffer[tau] < threshold) {
      // Find the local minimum
      while (tau + 1 < maxTau && yinBuffer[tau + 1] < yinBuffer[tau]) {
        tau++;
      }
      tauEstimate = tau;
      break;
    }
  }

  if (tauEstimate === -1) return null;

  // Step 4: Parabolic interpolation for sub-sample accuracy
  let betterTau: number;
  const x0 = tauEstimate < 1 ? tauEstimate : tauEstimate - 1;
  const x2 = tauEstimate + 1 < halfSize ? tauEstimate + 1 : tauEstimate;

  if (x0 === tauEstimate) {
    betterTau = yinBuffer[tauEstimate] <= yinBuffer[x2] ? tauEstimate : x2;
  } else if (x2 === tauEstimate) {
    betterTau = yinBuffer[tauEstimate] <= yinBuffer[x0] ? tauEstimate : x0;
  } else {
    const s0 = yinBuffer[x0];
    const s1 = yinBuffer[tauEstimate];
    const s2 = yinBuffer[x2];
    betterTau = tauEstimate + (s2 - s0) / (2 * (2 * s1 - s2 - s0));
  }

  return sampleRate / betterTau;
}

export function cleanup(): void {
  if (source) {
    source.disconnect();
    source = null;
  }
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop());
    mediaStream = null;
  }
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
  analyser = null;
  timeDomainBuffer = null;
  active = false;
}
